import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import {
    optClassification1,
    optClassification2,
    optClassification3,
    optProductiveStage,
    ClassificationOption,
} from '../../../common/utils/classification-data';
import Swal from 'sweetalert2';
import { CountryService } from '../../../services/country.service';
import { ClasificacionService } from '../../services/clasificacionProductos';
import { firstValueFrom } from 'rxjs';
import { ProductTypeResponseInterface } from 'src/interfaces/product-type-response.interface';

/**
 * Alias con el que el backend devuelve los datos específicos de cada tipo de
 * producto, indexado por el id de siriiagro.tipo_producto.
 *
 * Estos alias están definidos en las asociaciones de producto.model.ts y son
 * independientes del nombre visible del tipo de producto, por lo que no se
 * afectan cuando ese nombre cambia.
 */
const ASOCIACION_POR_TIPO_PRODUCTO: Record<string, string> = {
    '1': 'plaguicida',
    '2': 'fertilizante',
    '3': 'coadyuvante',
    '4': 'alimentoBalanceadoParaAnimales',
    '5': 'acondicionadorEnmienda',
};

@Component({
    selector: 'app-edit-product',
    templateUrl: './edit-product.component.html',
    styleUrls: ['./edit-product.component.css'],
})
export class EditProductComponent implements OnInit {
    @ViewChild('productForm') productForm!: NgForm;
    @Input()
    currentProductEdit: any;
    icaNumber: number = null;
    commercialName = '';
    selectedTypeProduct = '';
    private apiUrl: string = environment.apiSiriUrl;
    activeIngredient: any;
    toxicologicalClassification: any;
    productClassification: any;
    countryOrigin: any;
    concentration: any;
    classification1: any;
    optClassification2: any;
    classification2: any;
    classification3: any;
    optClassification3: any;
    fertiyCountryOrigin: any;
    fertiyTypeFormulation: any;
    fertiConcentration: any;
    coadyuvanteCountryOrigin: any;
    coadyuvanteClassification: any;
    formulationTypeId: number = null;
    balanceFoodSpecie: any;
    balanceFoodProductiveStage: any;
    optProductClassification1: any;
    eslabon = '';
    countries: any[];
    casaProductora: any;
    origenDelProducto: any;
    ingredienteActivo: any;
    optSpecieBalanceFood: any[] = [];
    optProductiveStage: ClassificationOption[] = optProductiveStage;
    optProdClassCoad: any[];
    optClassification1: ClassificationOption[] = [];
    optToxicologicalClassification: ClassificationOption[] = [];
    optToxicologicalClassification1: ClassificationOption[] = [];
    optFormulationType: any[];
    isIcaNumberDisabled: boolean;
    acondicionadorEnmiendaCountryOrigin: any;

    constructor(
        private router: Router,
        private http: HttpClient,
        private activatedRoute: ActivatedRoute,
        private countryService: CountryService,
        private clasificacion: ClasificacionService
    ) {}

    async ngOnInit() {
        const id = this.activatedRoute.snapshot.paramMap.get('id');
        this.loadSpeciesData();
        this.loadToxicologicalClassification();
        this.loadClasification1();
        this.loadClassificationProduct();
        this.loadFormulationType();
        this.loadCountries();
        this.loadClassificationProductCoadyuvante();
        await this.getProductData(id);
        this.isIcaNumberDisabled = await this.validarHistorico(
            this.currentProductEdit.id
        );
    }

    async getProductData(id) {
        return new Promise<void>((resolve, reject) => {
            this.http.get<any>(`${this.apiUrl}producto/${id}`).subscribe(
                (response) => {
                    this.currentProductEdit = response;
                    this.optToxicologicalClassification =
                        this.optToxicologicalClassification1;
                    this.optClassification2 = optClassification2;
                    this.optClassification3 = optClassification3;
                    if (this.currentProductEdit) {
                        this.icaNumber =
                            this.currentProductEdit.numeroRegistroIca;
                        this.origenDelProducto =
                            this.currentProductEdit.OrigenDelProducto;
                        this.commercialName =
                            this.currentProductEdit.nombreComercial;
                        this.casaProductora =
                            this.currentProductEdit.casaProductora;
                        this.selectedTypeProduct =
                            this.currentProductEdit.tipoProductoId.toString();
                    }

                    const productActions = {
                        '1': (data) => {
                            this.activeIngredient = data.ingredienteActivo;
                            this.toxicologicalClassification =
                                data.clasificacionToxicologicaId;
                            this.productClassification =
                                data.clasificacionProductoId;
                            this.countryOrigin = data.paisDeOrigen;
                            this.formulationTypeId = data.tipoFormulacionId;
                            this.concentration = data.concentracion;
                        },
                        '2': (data) => {
                            console.log('data', data);
                            this.classification1 = data.clasificacion1Id;
                            this.classification2 = data.clasificacion_2;
                            this.classification3 = data.clasificacion_3;
                            this.fertiyCountryOrigin = data.paisDeOrigen;
                            this.fertiyTypeFormulation = data.tipoFormulacion;
                            this.fertiConcentration = data.concentracion;
                        },
                        '3': (data) => {
                            this.coadyuvanteCountryOrigin = data.paisDeOrigen;
                            this.coadyuvanteClassification =
                                data.clasificacionProductoCoadyuvanteId;
                            this.ingredienteActivo = data.ingrediente_activo;
                        },
                        '4': (data) => {
                            console.log('data', data.especieId);
                            this.balanceFoodSpecie = data.especieId;
                            this.balanceFoodProductiveStage =
                                data.etapaProductiva;
                        },
                        '5': (data) => {
                            this.acondicionadorEnmiendaCountryOrigin =
                                data.paisDeOrigen;
                        },
                    };

                    // Antes la clave de la asociación se derivaba camelizando
                    // tipoProducto.nombre, lo cual coincidía con el alias del
                    // backend solo por casualidad. Al renombrar el tipo 4 a
                    // "Nutrición animal" (HU130) producía "nutriciónAnimal",
                    // una propiedad inexistente, y fallaba al leer el índice 0.
                    // Ahora se resuelve por id, que es estable.
                    const asociacion =
                        ASOCIACION_POR_TIPO_PRODUCTO[this.selectedTypeProduct];
                    const datosEspecificos =
                        this.currentProductEdit[asociacion]?.[0];

                    if (
                        productActions[this.selectedTypeProduct] &&
                        datosEspecificos
                    ) {
                        productActions[this.selectedTypeProduct](
                            datosEspecificos
                        );
                    }

                    resolve();
                },
                (error) => {
                    console.error('Error al cargar el producto', error);
                    reject(error);
                }
            );
        });
    }

    loadCountries() {
        this.countryService.getCountries().subscribe(
            (response) => {
                this.countries = response;
            },
            (error) => {
                console.error('Error loading countries', error);
            }
        );
    }
    //carga de clasificacion de productos coadyuvantes
    async loadClassificationProductCoadyuvante(): Promise<void> {
        try {
            const response = await firstValueFrom(
                this.http.get<ProductTypeResponseInterface[]>(
                    `${this.apiUrl}tipo-producto/coadyuvante-clasificacion-producto`
                )
            );
            this.optProdClassCoad = response;
            console.log(response);
        } catch (error) {
            console.error(
                'Error al cargar opciones de calsificacion del producto coadyuvante',
                error
            );
        }
    }

    private loadSpeciesData() {
        this.http.get<any[]>(`${this.apiUrl}tipo-producto/especies`).subscribe(
            (response) => {
                this.optSpecieBalanceFood = response;
            },
            (error) => {
                console.error('Error al cargar opciones de Dropdown 2', error);
            }
        );
    }

    async loadToxicologicalClassification() {
        this.optToxicologicalClassification1 = await firstValueFrom(
            this.clasificacion.loadToxicologicalClassification()
        );
    }

    async loadClasification1() {
        this.optClassification1 = await firstValueFrom(
            this.clasificacion.optClassification1()
        );
    }

    private loadClassificationProduct() {
        this.http
            .get<
                any[]
            >(`${this.apiUrl}tipo-producto/plaguicida-clasificacion-producto`)
            .subscribe(
                (response) => {
                    this.optProductClassification1 = response;
                },
                (error) => {
                    console.error(
                        'Error al cargar opciones de Dropdown 1',
                        error
                    );
                }
            );
    }

    private loadFormulationType() {
        this.http
            .get<
                any[]
            >(`${this.apiUrl}tipo-producto/tipo-formulacion-plaguicida`)
            .subscribe(
                (response) => {
                    this.optFormulationType = response;
                },
                (error) => {
                    console.error(
                        'Error al cargar opciones de Dropdown 1',
                        error
                    );
                }
            );
    }

    async validarHistorico(id: number): Promise<boolean> {
        try {
            return await firstValueFrom(
                this.http.get<boolean>(
                    `${this.apiUrl}producto/validar-historico/${id}`
                )
            );
        } catch (error) {
            console.error('Error al validar el histórico del producto:', error);

            return false;
        }
    }

    async onSubmit() {
        this.productForm.form.markAllAsTouched();

        if (!this.icaNumber || !this.commercialName || !this.casaProductora) {
            Swal.fire(
                'Error',
                'Por favor completa todos los campos requeridos correctamente',
                'error'
            );
            return;
        }

        let isValid = true;
        let errorMessage = '';

        switch (this.selectedTypeProduct) {
            case '1': // Plaguicidas
                if (!this.origenDelProducto) {
                    isValid = false;
                    errorMessage =
                        'Origen del producto es requerido';
                }
                break;
            case '2': // Fertilizantes
                if (!this.origenDelProducto) {
                    isValid = false;
                    errorMessage =
                        'Origen del producto son requeridos';
                }
                break;
            case '3': // Coadyuvantes
                if (!this.origenDelProducto) {
                    isValid = false;
                    errorMessage = 'Origen del producto es requerido';
                }
                break;
            case '5': // Acondicionador o enmienda
                if (!this.origenDelProducto) {
                    isValid = false;
                    errorMessage = 'Origen del producto es requerido';
                }
                break;
        }

        if (!isValid) {
            Swal.fire('Error', errorMessage, 'error');
            return;
        }

        const formData = {
            tipoProductoId: +this.selectedTypeProduct,
            agenteVigiladoId: this.currentProductEdit.agenteVigiladoId,
            nombreComercial: this.commercialName,
            numeroRegistroIca: this.icaNumber,
            OrigenDelProducto: this.origenDelProducto,
            casaProductora: this.casaProductora,
            tipoProductoEspecifico: {
                tipo: '',
                data: {},
            },
        };

        if (this.origenDelProducto === 'Producto Nacional') {
            this.countryOrigin = ' ';
            this.fertiyCountryOrigin = ' ';
            this.coadyuvanteCountryOrigin = ' ';
            this.acondicionadorEnmiendaCountryOrigin = ' ';
        }

        if (this.classification2 === 'Orgánico') {
            this.classification3 = ' ';
        }

        switch (this.selectedTypeProduct) {
            case '1':
                if (!this.origenDelProducto) {
                    Swal.fire(
                        'El campo origen del producto es requerido.',
                        '',
                        'error'
                    );
                    return;
                } else {
                    formData.tipoProductoEspecifico.tipo = 'Plaguicida';
                    formData.tipoProductoEspecifico.data = {
                        ingredienteActivo: this.activeIngredient,
                        clasificacionToxicologicaId:
                            this.toxicologicalClassification,
                        clasificacionProductoId: this.productClassification,
                        paisDeOrigen: this.countryOrigin,
                        OrigenDelProducto: this.origenDelProducto,
                        tipoFormulacionId: this.formulationTypeId,
                        concentracion: this.concentration,
                    };
                }
                break;

            case '2':
                if (!this.origenDelProducto) {
                    Swal.fire(
                        'Los campos origen del producto son requeridos.',
                        '',
                        'error'
                    );
                    return;
                } else {
                    formData.tipoProductoEspecifico.tipo = 'Fertilizante';
                    formData.tipoProductoEspecifico.data = {
                        paisDeOrigen: this.fertiyCountryOrigin,
                        clasificacion1Id: this.classification1,
                        clasificacion_2: this.classification2,
                        clasificacion_3: this.classification3,
                        tipoFormulacion: this.fertiyTypeFormulation,
                        concentracion: this.fertiConcentration,
                    };
                }
                break;
            case '3':
                if (!this.origenDelProducto) {
                    Swal.fire(
                        'El campo origen del producto es requerido.',
                        '',
                        'error'
                    );
                    return;
                } else {
                    formData.tipoProductoEspecifico.tipo = 'Coadyuvante';
                    formData.tipoProductoEspecifico.data = {
                        paisDeOrigen: this.coadyuvanteCountryOrigin,
                        clasificacionProductoCoadyuvanteId:
                            this.coadyuvanteClassification,
                        ingrediente_activo: this.ingredienteActivo,
                    };
                    break;
                }
            case '4':
                // HU130: el tipo de producto id 4 se denomina "Nutrición animal".
                formData.tipoProductoEspecifico.tipo = 'Nutrición animal';
                formData.OrigenDelProducto = 'Producto Nacional';
                formData.tipoProductoEspecifico.data = {
                    especieId: this.balanceFoodSpecie,
                    etapaProductiva: this.balanceFoodProductiveStage,
                };

                break;
            case '5':
                if (!this.origenDelProducto) {
                    Swal.fire(
                        'El campo origen del producto es requerido.',
                        '',
                        'error'
                    );
                    return;
                } else {
                    formData.tipoProductoEspecifico.tipo =
                        'Acondicionador o enmienda';
                    formData.tipoProductoEspecifico.data = {
                        paisDeOrigen: this.acondicionadorEnmiendaCountryOrigin,
                    };
                    break;
                }
        }

        console.log('formData..', formData);

        this.http
            .put(
                `${this.apiUrl}producto/${this.currentProductEdit.id}`,
                formData
            )
            .subscribe(
                (response) => {
                    this.router.navigate(['consultar']);
                },
                (error) => {
                    console.error(
                        'Error al actualizar los datos del producto',
                        error
                    );
                }
            );
        try {
            const response = await firstValueFrom(
                this.http.put(
                    `${this.apiUrl}producto/${this.currentProductEdit.id}`,
                    formData
                )
            );

            Swal.fire(
                '¡Éxito!',
                'Cambios guardados correctamente',
                'success'
            ).then(() => this.router.navigate(['consultar']));
        } catch (error) {
            console.error(
                'Error  al actualizar los datos del producto:',
                error
            );
            Swal.fire(
                'Error',
                error.error?.message
                    ? error.error?.message
                    : 'Error al guardar. Por favor intente nuevamente.',
                'error'
            );
        }
    }
}
